import { buildModalOverlay } from './buildElements.js';
import { modalState, closeEvents, buttonEvents } from './eventsHandlers.js';
import { cssClasses } from './variables.js';

function closeModal() {
    const { body, modals, modalOverlay: overlay, animationSpeed } = modalState;

    if (!modals || modals.length === 0) return; // No modals to close

    // Find the active modal
    const activeModal = modals.find((modal) => modal.classList.contains(cssClasses.isActive));
    if (!activeModal) return; 

    // Find active button
    const activeButton = document.querySelector('[data-modalButton].isActive');
    const closeButtons = activeModal.querySelectorAll('[data-modalClose]');

    // Dispatch the `modalClosed` event
    const modalClosed = activeModal.querySelector('[data-modal-open]')?.dataset.modalOpen;
    if (modalClosed) {
        document.dispatchEvent(
            new CustomEvent('modalClosed', {
                bubbles: true,
                detail: { modalName: modalClosed },
            })
        );
    }

    // Reset modal styles and attributes
    activeModal.classList.remove(cssClasses.isActive, cssClasses.isAnimatingIn, cssClasses.isAnimatingOut);
    activeModal.setAttribute('aria-hidden', 'true');

    if (overlay) {
        overlay.classList.remove(cssClasses.isActive);
        overlay.removeEventListener('click', closeEvents);
    }

    // Remove event listeners from close buttons
    closeButtons.forEach((button) => button.removeEventListener('click', closeEvents));

    // Reset body styles and classes
    if (body) {
        body.classList.remove(cssClasses.modelIsVisible);
        body.style.removeProperty('--cModalAnimationSpeed');
    }

    // Reset the active button's state
    if (activeButton) {
        activeButton.classList.remove(cssClasses.isActive);
        activeButton.setAttribute('aria-expanded', 'false');
    }
}

function disposeModalJS() {
    const { body, modals, modalOverlay: overlay } = modalState;
    if (!body) return; // Nothing to dispose

    // Remove event listeners from modal buttons
    const modalButtons = Array.from(document.querySelectorAll('[data-modalButton="single"][data-modal-bound="true"]'));
    modalButtons.forEach(button => {
        button.removeEventListener('click', buttonEvents);
        button.removeAttribute('data-modal-bound');
    });

    // Remove overlay click event
    if (overlay) {
        overlay.removeEventListener('click', closeEvents);
        // Remove overlay from DOM
        overlay.remove();
    }

    // Remove close button events and clean up modals
    modals.forEach(modal => {
        const closeButtons = modal.querySelectorAll('[data-modalClose]');
        closeButtons.forEach(button => {
            button.removeEventListener('click', closeEvents);
        });
    });

    // Remove body styles and classes
    body.style.removeProperty('--cModalAnimationSpeed');
    body.classList.remove(cssClasses.modelIsVisible);

    // Clean up active states
    const activeButtons = document.querySelectorAll('[data-modalButton].isActive');
    activeButtons.forEach(button => {
        button.classList.remove(cssClasses.isActive);
        button.setAttribute('aria-expanded', 'false');
    });

    modals.forEach(modal => {
        modal.classList.remove(
            cssClasses.isActive,
            cssClasses.isAnimatingIn,
            cssClasses.isAnimatingOut
        );
        modal.setAttribute('aria-hidden', 'true');
    });

    // Reset modalState
    modalState.body = null;
    modalState.modals = [];
    modalState.modalOverlay = null;
    modalState.animationSpeed = 0;

    // Clean up closeEvents properties
    delete closeEvents.body;
    delete closeEvents.modals;
    delete closeEvents.modalOverlay;
    delete closeEvents.animationSpeed;
}

function modalJS() {
    const body = document.body;
    const modalButtons = Array.from(document.querySelectorAll('[data-modalButton="single"]:not([data-modal-bound="true"])'));
    // If there are no modals, end!!!
    if (modalButtons.length <= 0) return;

    const animationSpeed = 200;


    body.style.setProperty('--cModalAnimationSpeed', `${animationSpeed}ms`);
    const modals = Array.from(document.querySelectorAll('[data-modal]'));
    const overlay = buildModalOverlay(body);

    // Store state in module-level object instead of on function
    modalState.body = body;
    modalState.modals = modals;
    modalState.modalOverlay = overlay;
    modalState.animationSpeed = animationSpeed;

    // Pass references/variables into the close events function
    closeEvents.body = body;
    closeEvents.modals = modals;
    closeEvents.modalOverlay = overlay;
    closeEvents.animationSpeed = animationSpeed;

    modalButtons.forEach(button => {
        // Mark the trigger as "bound" to avoid duplicate listeners
        button.setAttribute('data-modal-bound', 'true');
        button.addEventListener('click', buttonEvents);
    });
}

// Self-invoke the function
modalJS();

// Export the function for potential use in other files
export { modalJS, disposeModalJS, closeModal };